/**
 * Completed Batch Service
 * Service for fetching completed purification batches from purification-service
 * These completed batches are used to create new inventory batches
 *
 * @author SAUBHAGYA Development Team
 * @since Batch Creation Enhancement
 */

/**
 * Get JWT token from localStorage
 */
function getAuthToken(): string | null {
  return localStorage.getItem('saubhagya_jwt_token');
}

/**
 * Get authorization headers
 */
function getHeaders(): HeadersInit {
  const token = getAuthToken();
  return {
    'Content-Type': 'application/json',
    ...(token && { 'Authorization': `Bearer ${token}` })
  };
}

/**
 * Purification Batch Response Type
 * Represents a completed batch from purification service
 * Fields match actual API response from /api/v1/batches/status/COMPLETED
 */
export interface CompletedPurificationBatch {
  id: number;
  batchCode: string;
  batchStatus: string;
  priority: string;
  inputVolumeM3: number;
  receivedDate: string;
  unitId: number;
  sourceBatchNumber?: string;
  // Formatted display fields from API
  formattedInputVolume: string;
  formattedEfficiency: string;
  formattedOutputVolume: string;
  // Terminal state and color coding for UI
  terminalState: boolean;
  priorityColor: string;
  statusColor: string;
  qualityColor: string;
  // Quality and purity fields
  qualityGrade?: string;
  postMethanePercent?: number;
  preMethanePercent?: number;
  conversionEfficiencyPercent?: number;
  // Optional fields that may or may not be present in response
  completedAt?: string;
  isActive?: boolean;
}

/**
 * Pagination Response for batch list
 */
export interface PaginatedBatchResponse {
  content: CompletedPurificationBatch[];
  totalElements: number;
  totalPages: number;
  size: number;
  number: number;
  empty: boolean;
}

const PURIFICATION_BASE_URL = 'http://localhost:8087/purification-service/api/v1/batches';

/**
 * Service to handle completed batch operations
 */
class CompletedBatchService {
  /**
   * Fetch all completed batches from purification service
   * @param page - Page number (0-indexed)
   * @param size - Page size
   * @returns Paginated list of completed batches
   */
  async getCompletedBatches(
    page: number = 0,
    size: number = 50
  ): Promise<PaginatedBatchResponse> {
    try {
      const queryParams = new URLSearchParams({
        page: page.toString(),
        size: size.toString(),
        sort: 'completedAt,desc'
      });

      const response = await fetch(
        `${PURIFICATION_BASE_URL}/status/COMPLETED?${queryParams}`,
        {
          method: 'GET',
          headers: getHeaders(),
          credentials: 'include',
          mode: 'cors'
        }
      );

      console.log('Fetch response status:', response.status);
      console.log('Fetch response headers:', response.headers);

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        console.warn('Purification service unavailable or returned error:', response.status, errorData);
        // Return empty paginated response instead of throwing
        return {
          content: [],
          totalElements: 0,
          totalPages: 0,
          size: size,
          number: page,
          empty: true
        };
      }

      const responseData = await response.json();
      console.log('Completed batches response:', responseData);
      // Unwrap the API response which is wrapped in { success: true, data: {...} }
      const result = responseData.data || responseData;
      console.log('Final batches result:', result);
      return result;
    } catch (error: any) {
      console.error('Failed to fetch completed batches:', error);
      // Return empty response instead of throwing to handle service unavailability gracefully
      return {
        content: [],
        totalElements: 0,
        totalPages: 0,
        size: size,
        number: page,
        empty: true
      };
    }
  }

  /**
   * Fetch a specific completed batch by ID
   * @param batchId - Purification batch ID
   * @returns Completed batch details
   */
  async getCompletedBatchById(batchId: number): Promise<CompletedPurificationBatch | null> {
    try {
      const response = await fetch(`${PURIFICATION_BASE_URL}/${batchId}`, {
        method: 'GET',
        headers: getHeaders(),
        credentials: 'include',
        mode: 'cors'
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        console.warn('Failed to fetch batch details:', errorData);
        return null;
      }

      const responseData = await response.json();
      // Unwrap the API response if wrapped in { data: {...} }
      return responseData.data || responseData;
    } catch (error: any) {
      console.error('Failed to fetch batch details:', error);
      return null;
    }
  }

  /**
   * Search completed batches by batch code
   * @param searchTerm - Batch code to search for
   * @returns List of matching completed batches
   */
  async searchCompletedBatches(searchTerm: string): Promise<CompletedPurificationBatch[]> {
    try {
      const queryParams = new URLSearchParams({
        search: searchTerm,
        page: '0',
        size: '100'
      });

      const response = await fetch(
        `${PURIFICATION_BASE_URL}/status/COMPLETED?${queryParams}`,
        {
          method: 'GET',
          headers: getHeaders(),
          credentials: 'include',
          mode: 'cors'
        }
      );

      if (!response.ok) {
        return [];
      }

      const data = await response.json();
      return data.content || [];
    } catch (error: any) {
      console.error('Failed to search completed batches:', error);
      return [];
    }
  }

  /**
   * Get recently completed batches (last 30 days)
   * @returns List of recently completed batches
   */
  async getRecentlyCompletedBatches(): Promise<CompletedPurificationBatch[]> {
    try {
      const queryParams = new URLSearchParams({
        page: '0',
        size: '20',
        sort: 'completedAt,desc'
      });

      const response = await fetch(
        `${PURIFICATION_BASE_URL}/status/COMPLETED?${queryParams}`,
        {
          method: 'GET',
          headers: getHeaders(),
          credentials: 'include',
          mode: 'cors'
        }
      );

      if (!response.ok) {
        return [];
      }

      const data = await response.json();
      return data.content || [];
    } catch (error: any) {
      console.error('Failed to fetch recently completed batches:', error);
      return [];
    }
  }
}

export const completedBatchService = new CompletedBatchService();
